import React, { useState, useContext } from "react";
import { useSelector } from "react-redux";
import { Modal, Form, Button, Alert } from "react-bootstrap";
import Select from "react-select";
import NewNumUsersDisplay from "../../../../components/wifi/NewNumUsersDisplay";
import useAuth from "../../../../hooks/useAuth";
import usePermissions from "../../../../hooks/usePermissions";
import axios from "axios";
import { apiConfig } from "../../../../config";
import NotyfContext from "../../../../contexts/NotyfContext";
import { ReactComponent as LogoLoader } from "../../../../assets/img/wavenet-animated-loader3.svg";

const AddNew8021XCredentialModal = ({
  openModals,
  toggle,
  customerOptions,
  dispatch,
  setRefreshData,
  loading,
}) => {
  const notyf = useContext(NotyfContext);
  const selectedLandlord = useSelector((state) => state.landlordswitcher);
  const { userAccessToken } = useAuth();
  const bearerToken = `Bearer ${userAccessToken}`;

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [excellid, setExcellId] = useState(null);
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [SSID, setSSID] = useState("");
  const [accessLevelNum, setAccessLevelNum] = useState(null);
  const [customVlanId, setCustomVlanId] = useState(undefined);
  const [validated, setValidated] = useState(false);

  const [numUsersIsLoading, setNumUsersIsLoading] = useState(true);
  const [numAvailable, setNumAvailable] = useState(0);
  const [numInUse, setNumInUse] = useState(0);

  const [availableSsids, setAvailableSsids] = useState([]);

  const canUseCustomVlan = usePermissions("edit:custom_vlan:all");
  const isCustomSelected = accessLevelNum === 2;

  React.useEffect(() => {
    axios
      .get(apiConfig.credentialsApiUrl + "ssids/enabled", {
        headers: { Authorization: bearerToken },
      })
      .then((response) => {
        const filteredSSIDs = response.data.ssids.rows
          .filter(
            (ssid) =>
              ssid.building_operator_id ===
                parseInt(selectedLandlord.landlordid) &&
              ssid.credential_type === "dot1x"
          )
          .map((ssid) => ({ value: ssid.ssid, label: ssid.ssid }));
        setAvailableSsids(filteredSSIDs);
      });
  }, [selectedLandlord.landlordid, bearerToken]);

  const resetFormFields = () => {
    setFirstName("");
    setLastName("");
    setUsername("");
    setEmail("");
    setAccessLevelNum(null);
    setCustomVlanId(undefined);
    setExcellId(null);
    setSSID(null);
    setValidated(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const form = event.target;

    if (!form.checkValidity()) {
      event.stopPropagation();
      setValidated(true);
      return;
    }

    const createCredentialBody = {
      username,
      firstname: firstName,
      lastname: lastName,
      email,
      excellid,
      SSID,
      wavenetIdList: [excellid],
      userlevel: accessLevelNum,
      custom_vlan: customVlanId,
      building_operator_id: selectedLandlord.landlordid,
      sendInviteEmail: true,
    };

    try {
      const response = await axios.post(
        apiConfig.credentialsApiUrl + "users/",
        createCredentialBody,
        { headers: { Authorization: bearerToken } }
      );
      if (response.data.success === 1) {
        notyf.open({
          type: "success",
          message: response.data.message,
          duration: 3000,
          ripple: true,
          dismissible: true,
          position: { x: "right", y: "top" },
        });
        toggle("addNewCredential");
        setTimeout(() => dispatch(setRefreshData()), 2000);
      }
    } catch (error) {
      notyf.open({
        type: "warning",
        message: error.response?.data?.error || "An error occurred.",
        duration: 3000,
        ripple: true,
        dismissible: true,
        position: { x: "right", y: "top" },
      });
    } finally {
      resetFormFields();
    }
  };

  return (
    <Modal
      show={openModals["addNewCredential"]}
      onHide={() => {
        toggle("addNewCredential");
        resetFormFields();
      }}
      centered
    >
      <Form noValidate validated={validated} onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <b>Admin Function:</b> Add New 802.1X Credential
        </Modal.Header>
        <Modal.Body className="m-3">
          <Form.Group className="mb-3">
            <Form.Label>
              Company Name
              {loading && (
                <LogoLoader
                  className="d-inline-block align-middle ms-2"
                  style={{ width: "20px", height: "20px" }}
                />
              )}
            </Form.Label>
            <Select
              className="react-select-container"
              classNamePrefix="react-select"
              options={customerOptions}
              placeholder="Select..."
              onChange={(e) => setExcellId(e.value)}
              value={
                excellid
                  ? customerOptions.find((o) => o.value === excellid)
                  : null
              }
            />
          </Form.Group>

          {excellid && (
            <NewNumUsersDisplay
              excellid={excellid}
              isLoading={numUsersIsLoading}
              setIsLoading={setNumUsersIsLoading}
              numAvailable={numAvailable}
              setNumAvailable={setNumAvailable}
              numInUse={numInUse}
              setNumInUse={setNumInUse}
            />
          )}

          {excellid && !numUsersIsLoading && numAvailable > 0 && (
            <>
              <hr />
              <Form.Group className="mb-3">
                <Form.Label>First Name</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="First Name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                />
                <Form.Control.Feedback type="invalid">
                  First name is required.
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Last Name</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Last Name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                />
                <Form.Control.Feedback type="invalid">
                  Last name is required.
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Username</Form.Label>
                <Form.Control
                  type="email"
                  placeholder="Username"
                  value={username}
                  onChange={(e) => {
                    setUsername(e.target.value.trim());
                    setEmail(e.target.value.trim());
                  }}
                  required
                />
                <Form.Control.Feedback type="invalid">
                  Please provide a valid email.
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>SSID</Form.Label>
                <Select
                  className="react-select-container"
                  classNamePrefix="react-select"
                  options={availableSsids}
                  onChange={(e) => setSSID(e.value)}
                  required
                />
                <Form.Control.Feedback type="invalid">
                  Please select an SSID.
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Access Level</Form.Label>
                <Select
                  className="react-select-container"
                  classNamePrefix="react-select"
                  options={
                    canUseCustomVlan[1] === true
                      ? [
                          { value: 2, label: "Custom" },
                          { value: 1, label: "Web & LAN (Vlan: Room Vlan)" },
                          { value: 0, label: "Web Only (Legacy) (Vlan: 769)" },
                        ]
                      : [
                          { value: 1, label: "Web & LAN (Vlan: Room Vlan)" },
                          { value: 0, label: "Web Only (Legacy) (Vlan: 769)" },
                        ]
                  }
                  onChange={(e) => {
                    setAccessLevelNum(e.value);
                    if (e.value !== 2) setCustomVlanId(undefined);
                  }}
                />
              </Form.Group>

              {canUseCustomVlan[1] === true && isCustomSelected && (
                <Form.Group className="mb-3">
                  <Form.Label>Custom Vlan</Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="Vlan ID"
                    value={customVlanId}
                    onChange={(e) => {
                      setCustomVlanId(e.target.value);
                    }}
                    required
                  />
                  <Form.Control.Feedback type="invalid">
                    Please provide a valid custom vlan
                  </Form.Control.Feedback>
                </Form.Group>
              )}
            </>
          )}

          {excellid && !numUsersIsLoading && numAvailable <= 0 && (
            <Alert className="my-3 p-2">
              This account does not have any available wi-fi users.{" "}
              <a href="#" onClick={() => toggle("wifiPurchase")}>
                Request more.
              </a>
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => toggle("addNewCredential")}
          >
            Cancel
          </Button>
          <Button type="submit" variant="info">
            Create Credential
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default AddNew8021XCredentialModal;
